import './ETFLanding.sass';
import Slider from 'react-slick';
import axios from 'axios';
import moment from 'moment';
import { useRef, useState, useMemo, useEffect, useCallback } from 'react';
import { sortBy, groupBy } from 'lodash';
import { Link } from 'react-router-dom';
import ETFChart from './ETFChart';
import Meta from '../components/Meta';
import BaseUrl from '../api/baseUrl';
import { useAds, useStockList, useArticles, useETFEstimate } from '../api';
import getTimestamp from '../library/tools/getTimestamp';

const ETFLanding = (props) => {
	const { routeData } = props;

	const heroSlides = useAds('ETFbanner');

	const isLoading = useMemo(() => {
		if (!heroSlides.isLoading) return false;
		return true;
	}, [heroSlides.isLoading]);

	const heroSlidesList = useMemo(() => {
		return sortBy(heroSlides.list, 'sortValue');
	}, [heroSlides]);

	const heroSlidesSettings = useMemo(() => {
		return {
			dots: true,
			infinite: true,
			arrows: false,
			speed: 800,
			slidesToShow: 1,
			slidesToScroll: 1,
			autoplay: true,
			autoplaySpeed: 5000,
			pauseOnHover: false,
			// fade: true,
			customPaging: (index) => (
				<div className='kv-dot'>
					<div className='text'>{heroSlidesList[index]?.name}</div>
					<div className='mobile-dot'></div>
				</div>
			),
		};
	}, [heroSlidesList]);

	const hotkeys = useAds('ETFhotkey');

	const [selectedEtf, setCurrentEtf] = useState('');

	const { list } = useStockList();

	const etfList = useMemo(() => {
		return list.filter((item) => {
			return item.ec001 === 3;
		});
	}, [list]);

	const groupedEtfList = useMemo(() => {
		return groupBy(etfList, 'ec020');
	}, [etfList]);

	const [activeTab, setActiveTab] = useState('all');

	const slider = useRef();

	const currentList = useMemo(() => {
		return groupedEtfList[activeTab] || etfList;
	}, [activeTab, etfList, groupedEtfList]);

	const tabs = useMemo(() => {
		const tabs = Object.keys(groupedEtfList);
		return tabs;
	}, [groupedEtfList]);

	const fundsSlideSettings = useMemo(() => {
		const max3 = currentList.length > 3 ? 3 : currentList.length || 1;
		const max1half = currentList.length > 1.8 ? 1.8 : currentList.length || 1;

		return {
			dots: false,
			infinite: true,
			arrows: false,
			speed: 300,
			slidesToScroll: 1,
			autoplay: false,
			slidesToShow: max3,
			pauseOnHover: false,
			swipeToSlide: true,
			responsive: [
				{
					breakpoint: 1023,
					settings: {
						slidesToShow: max1half,
						slidesToScroll: 1,
						infinite: false,
					},
				},
				{
					breakpoint: 767,
					settings: {
						slidesToShow: 1,
						slidesToScroll: 1,
						infinite: false,
						// fade: true,
					},
				},
			],
		};
	}, [currentList.length]);

	useEffect(() => {
		if (etfList.length > 0) {
			setCurrentEtf(etfList[0]?.result?.[0]?.fundID);
		}
	}, [etfList]);

	const { articles } = useArticles('', '/ETFlandingpage', '', '', '');

	const articlesMax3 = useMemo(() => {
		return articles.slice(0, 3);
	}, [articles]);

	const [currentItem, setCurrentItem] = useState('');
	const [currentItemObj, setCurrentItemObj] = useState({});

	const { apiData } = useETFEstimate();

	const [mobileEstimateList, setMobileEstimateList] = useState([]);

	const prevCurrentItem = useRef(currentItem);
	const initialized = useRef(false);

	useEffect(() => {
		if (apiData && apiData.length && !initialized.current) {
			initialized.current = true;
			setCurrentItem(apiData[0].fundID);
			setCurrentItemObj(apiData[0]);

			const first2Items = apiData.slice(0, 2);
			setMobileEstimateList(first2Items);
		}

		if (currentItem !== prevCurrentItem.current && apiData.length && initialized.current) {
			const currentItemObj = apiData.find((item) => item.fundID === currentItem);
			setCurrentItemObj(currentItemObj);
		}
	}, [apiData, currentItem]);

	const [graphData, setGraphData] = useState({
		series1: [],
		series2: [],
		categories: [],
	});

	const processGraphData = (data) => {
		const series1 = [];
		const series2 = [];
		const categories = [];

		data.forEach((item) => {
			const { nNav, nDate, mnNav } = item;
			const timestamp = getTimestamp(nDate);
			const formattedDate = moment(timestamp).format('HH:mm:ss');

			// Skip invalid dates
			if (!moment(timestamp).isValid()) {
				return;
			}

			const nNavNum = Number(nNav);
			const mnNavNum = Number(mnNav);
			series1.push(nNavNum);
			series2.push(mnNavNum);
			categories.push(formattedDate);
		});

		setGraphData({
			series1,
			series2,
			categories,
		});
	};

	const getGraphData = useCallback((fundId) => {
		let url = '';
		if (BaseUrl === '/') {
			const { href } = window.location;
			const split = href.split('/').slice(0, -1);
			const join = split.join('/');

			url = join;
		}
		axios.get(`${url || BaseUrl}/api/WebSite/Estimatehistory?fundID=${fundId}`).then((response) => {
			if (response?.status === 200) {
				processGraphData(response.data?.result || []);
			}
		});
	}, []);

	useEffect(() => {
		if (currentItem) {
			// First clear the graph data to make it empty before redrawing
			// setGraphData({
			// 	series1: [],
			// 	series2: [],
			// 	categories: [],
			// });

			setGraphData((prevData) => ({
				series1: prevData.series1.map(() => 0),
				series2: prevData.series2.map(() => 0),
				categories: [...prevData.categories],
			}));

			// Then fetch new data
			getGraphData(currentItem);

			// // 設置每15秒更新一次數據的定時器
			// const intervalId = setInterval(() => {
			// 	getGraphData(currentItem);
			// }, 15000);

			// // 組件卸載或currentItem變更時清除定時器
			// return () => {
			// 	clearInterval(intervalId);
			// };
		}
	}, [currentItem, getGraphData]);

	useEffect(() => {
		// 組件掛載時確保輪播器正確初始化
		if (slider.current) {
			// 短暫延遲以確保DOM完全渲染
			setTimeout(() => {
				slider.current.slickGoTo(0);
			}, 100);
		}

		// 視窗大小變化時重新初始化輪播器
		const handleResize = () => {
			if (slider.current) {
				slider.current.slickGoTo(0);
			}
		};

		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	// 當列表或活動標籤變化時重新初始化輪播器
	useEffect(() => {
		if (slider.current) {
			// 確保輪播器重新初始化並顯示第一張幻燈片
			setTimeout(() => {
				slider.current.slickGoTo(0);
			}, 100);
		}
	}, [currentList, activeTab]);

	const [popupOpen, setPopupOpen] = useState(false);

	// The effect that appended data to the chart has been removed
	// Now the chart will completely redraw when switching between ETFs

	return (
		<div className={`ETFLanding ${isLoading ? 'hidePage' : ''}`}>
			<Meta data={routeData} />

			<div className='kv-slide-wrap'>
				<Slider {...heroSlidesSettings} className='kv'>
					{heroSlidesList.map((slide) => {
						const { url, guid, pic, picM, descriptionPosition, urlType, description, description2 } = slide;

						if (urlType === '_self') {
							return (
								<Link to={url} className='slide' key={guid}>
									<div className='slide-content'>
										<img src={pic} alt='' className='desktop' />
										<img src={picM} alt='' className='mobile' />
										<div className={`text-wrap ${descriptionPosition}`}>
											<div className='title' dangerouslySetInnerHTML={{ __html: description }}></div>
											<div className='sub' dangerouslySetInnerHTML={{ __html: description2 }}></div>
										</div>
									</div>
								</Link>
							);
						}

						return (
							<a href={url} target='_blank' rel='noreferrer noopener' className='slide' key={guid}>
								<div className='slide-content'>
									<img src={pic} alt='' className='desktop' />
									<img src={picM} alt='' className='mobile' />
									<div className={`text-wrap ${descriptionPosition}`}>
										<div className='title' dangerouslySetInnerHTML={{ __html: description }}></div>
										<div className='sub' dangerouslySetInnerHTML={{ __html: description2 }}></div>
									</div>
								</div>
							</a>
						);
					})}
				</Slider>
			</div>

			<div className='pad-wrap'>
				<div className='common-pad-wrap first'>
					<div className='section-heading'>
						<div className='icon icon1'></div>
						<div className='text'>常用服務</div>
					</div>
				</div>

				<div className='common-services-wrap'>
					<div className='flex-hotkeys'>
						{hotkeys.list.map((item) => {
							const { guid, pic, name, url, urlType } = item;

							let widthClass = '';

							if (hotkeys.list.length === 2) widthClass = 'half';
							if (hotkeys.list.length === 3) widthClass = 'third';

							if (urlType === '_self') {
								return (
									<Link to={url} className={`hotkey ${widthClass}`} key={guid}>
										<div className='image-wrap'>
											<img src={pic} alt='' />
										</div>
										<div className='text'>{name}</div>
									</Link>
								);
							}

							return (
								<a href={url} target='_blank' rel='noreferrer noopener' className={`hotkey ${widthClass}`} key={guid}>
									<div className='image-wrap'>
										<img src={pic} alt='' />
									</div>
									<div className='text'>{name}</div>
								</a>
							);
						})}
					</div>

					<div className='select-box'>
						<div className='group'>
							<div className='group-title'>選擇基金</div>

							<div className='select-flex'>
								<select
									className='category grow'
									value={selectedEtf}
									onChange={(event) => {
										setCurrentEtf(event.target.value);
									}}>
									{etfList.map((item) => {
										return (
											<option value={item.result?.[0]?.fundID} key={item.id}>
												{item.twName}
											</option>
										);
									})}
								</select>
							</div>
						</div>

						<div className='button-flex'>
							<Link to={{ pathname: `/fund/etf/${selectedEtf}`, state: { tab: 'data' } }} className='button-wrap'>
								<div className='button'>淨值查詢</div>
							</Link>

							<Link to={{ pathname: `/fund/etf/${selectedEtf}`, state: { tab: 'dividend' } }} className='button-wrap'>
								<div className='button'>配息查詢</div>
							</Link>
						</div>
					</div>
				</div>

				<div className='common-pad-wrap'>
					<div className='section-heading'>
						<div className='icon icon2'></div>
						<div className='text'>ETF總覽</div>
					</div>
				</div>

				<div className='overview-wrap'>
					<div className='flex-controls'>
						<div className='tab-headings'>
							<div
								className={`tab ${activeTab === 'all' ? 'active' : ''}`}
								onClick={() => {
									setActiveTab('all');
								}}>
								全部
							</div>

							{tabs.map((tab) => {
								return (
									<div className='group' key={tab}>
										<div className='separator'></div>
										<div
											className={`tab ${activeTab === tab ? 'active' : ''}`}
											onClick={() => {
												setActiveTab(tab);
											}}>
											{tab}
										</div>
									</div>
								);
							})}

							{/* <div className='separator'></div>
							<div className={`tab ${activeTab === 'stock' ? 'active' : ''}`} onClick={() => { setActiveTab('stock') }}>股票型</div>
							<div className='separator'></div>
							<div className={`tab ${activeTab === 'bond' ? 'active' : ''}`} onClick={() => { setActiveTab('bond') }}>債券型</div> */}
						</div>

						<div className='navs'>
							<div
								className='prev'
								onClick={() => {
									slider.current.slickPrev();
								}}></div>
							<div
								className='next'
								onClick={() => {
									slider.current.slickNext();
								}}></div>
						</div>
					</div>

					<Slider {...fundsSlideSettings} className='funds' ref={slider}>
						{currentList.map((item) => {
							const result = item.result?.[0];
							const { guid, twName, etf002, navDate, nav, change, changeRange, fundID } = result;

							let condition = '';

							if (changeRange > 0) condition = 'bull';
							if (changeRange < 0) condition = 'bear';

							const fixedChangeRange = (changeRange * 100).toFixed(2).replace('-', '');

							return (
								<Link to={`/fund/etf/${fundID}`} className='slide-item' key={guid}>
									<div className='name'>{twName}</div>
									<div className='code'>{etf002}</div>
									<div className='info-wrap'>
										<div className='row'>
											<div className='head'>淨值日期</div>
											<div className='value'>{navDate}</div>
										</div>
										<div className='row'>
											<div className='head'>淨值</div>
											<div className='value'>{nav}</div>
										</div>
										<div className='row'>
											<div className='head'>漲跌/漲跌幅</div>
											<div className='value'>
												<div className={`range ${condition}`}>
													<div className='text'>{change}</div>
													<div className='slash'>/</div>
													<div className='color'>{fixedChangeRange}%</div>
													<div className='icon'></div>
												</div>
											</div>
										</div>
									</div>
									<div className='more-wrap'>
										<div className='more'>
											<div className='text'>看更多</div>
											<div className='icon-wrap'>
												<img src='/assets/link.svg' alt='' />
											</div>
										</div>
									</div>
								</Link>
							);
						})}
					</Slider>
				</div>

				<div className='common-pad-wrap'>
					<div className='section-heading'>
						<div className='icon icon3'></div>
						<div className='text'>即時預估淨值</div>
					</div>
				</div>

				{apiData && (
					<div className='estimate-wrap-desktop'>
						<div className='graph-wrap'>
							<div className='name'>{currentItemObj.twNameFull}</div>
							<div className='code'>{currentItemObj.etf002}</div>
							<div className='graph'>
								<ETFChart data={graphData} />
							</div>
						</div>

						<div className='table-wrap'>
							<table>
								<thead>
									<tr>
										<th rowSpan='2'>
											證券代碼<div className='border-bottom'></div>
											<div className='border-right'></div>
										</th>
										<th rowSpan='2'>
											基金名稱<div className='border-bottom'></div>
											<div className='border-right'></div>
										</th>
										<th colSpan='2'>
											淨值<div className='border-bottom'></div>
											<div className='border-right'></div>
										</th>
										<th colSpan='2'>
											市價<div className='border-bottom'></div>
											<div className='border-right'></div>
										</th>
										<th colSpan='1'>
											折溢價<div className='border-bottom'></div>
										</th>
									</tr>

									<tr>
										<th>
											預估
											<br />
											淨值
											<div className='border-bottom'></div>
											<div className='border-right'></div>
										</th>
										<th>
											漲跌幅
											<div className='border-bottom'></div>
											<div className='border-right'></div>
										</th>
										<th>
											最新
											<br />
											市價
											<div className='border-bottom'></div>
											<div className='border-right'></div>
										</th>
										<th>
											漲跌幅
											<div className='border-bottom'></div>
											<div className='border-right'></div>
										</th>
										<th className='border-right'>
											幅度
											<div className='border-bottom'></div>
										</th>
									</tr>
								</thead>

								<tbody>
									{apiData.map((item) => {
										const { guid, etf002, twNameFull, nNav, nDates, changeRange, mnNav, mChangeRange, pChangeRange, mnDate, fundID } = item;

										const getBullBear = (string) => {
											const trimmed = string.replace('%', '');
											const num = Number(trimmed);
											const isNumber = !Number.isNaN(num);

											if (isNumber && num > 0) return 'bull';
											if (isNumber && num < 0) return 'bear';

											return '';
										};

										return (
											<tr
												key={guid}
												className={currentItem === fundID ? 'active' : ''}
												onClick={() => {
													setCurrentItem(fundID);
													setCurrentItemObj(item);
												}}>
												<td className='sp'>{etf002}</td>

												<td className='left sp'>{twNameFull}</td>

												<td>
													{nNav}
													<div className='light'>{nDates}</div>
												</td>

												<td>
													<div className={`range ${getBullBear(changeRange)}`}>
														<div className='text'>{changeRange}</div>
														<div className='icon'></div>
													</div>
												</td>

												<td>
													{mnNav}
													<div className='light'>{mnDate}</div>
												</td>

												<td>
													<div className={`range ${getBullBear(mChangeRange)}`}>
														<div className='text'>{mChangeRange}</div>
														<div className='icon'></div>
													</div>
												</td>

												<td>
													<div className={`range ${getBullBear(pChangeRange)}`}>
														<div className='text'>{pChangeRange}</div>
														<div className='icon'></div>
													</div>
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					</div>
				)}

				<div className='exchange-rate-wrap-desktop'>
					<div className='exchange-rate'>{currentItemObj.dExchangeRate}</div>
				</div>

				{mobileEstimateList.length > 0 && (
					<div className='estimate-wrap-mobile'>
						{mobileEstimateList.map((item) => {
							const { guid, etf002, twNameFull, nNav, nDates, changeRange, mnNav, mChangeRange, pChangeRange, mnDate, fundID } = item;

							const getBullBear = (string) => {
								const trimmed = string.replace('%', '');
								const num = Number(trimmed);
								const isNumber = !Number.isNaN(num);

								if (isNumber && num > 0) return 'bull';
								if (isNumber && num < 0) return 'bear';

								return '';
							};

							return (
								<div
									key={guid}
									className={`item ${currentItem === fundID ? 'active' : ''}`}
									onClick={() => {
										setCurrentItem(fundID);
										setCurrentItemObj(item);
									}}>
									<div className='name'>{twNameFull}</div>
									<div className='code'>{etf002}</div>
									<div className='info-wrap'>
										<div className='group'>
											<div className='group-name'>淨值</div>
											<div className='row'>
												<div className='head'>預估淨值</div>
												<div className='value'>
													{nNav} <span className='light'>{nDates}</span>
												</div>
											</div>
											<div className='row'>
												<div className='head'>漲跌幅</div>
												<div className='value'>
													<div className={`range ${getBullBear(changeRange)}`}>
														<div className='text'>{changeRange}</div>
														<div className='icon'></div>
													</div>
												</div>
											</div>
										</div>

										<div className='separator'></div>

										<div className='group'>
											<div className='group-name'>市價</div>
											<div className='row'>
												<div className='head'>最新市價</div>
												<div className='value'>
													{mnNav} <span className='light'>{mnDate}</span>
												</div>
											</div>
											<div className='row'>
												<div className='head'>漲跌幅</div>
												<div className='value'>
													<div className={`range ${getBullBear(changeRange)}`}>
														<div className='text'>{mChangeRange}</div>
														<div className='icon'></div>
													</div>
												</div>
											</div>
										</div>

										<div className='separator'></div>

										<div className='group'>
											<div className='group-name'>折溢價</div>
											<div className='row'>
												<div className='head'>幅度</div>
												<div className='value'>
													<div className={`range ${getBullBear(pChangeRange)}`}>
														<div className='text'>{pChangeRange}</div>
														<div className='icon'></div>
													</div>
												</div>
											</div>
										</div>
									</div>
									<div
										className='show-graph'
										onClick={() => {
											setPopupOpen(true);
											setCurrentItem(fundID);
											setCurrentItemObj(item);
										}}>
										<div className='icon-wrap'>
											<img src='/graph.svg' alt='' />
										</div>
										<div className='text'>查看圖表</div>
									</div>
								</div>
							);
						})}

						{mobileEstimateList?.length !== apiData?.length && (
							<div className='more-button-wrap'>
								<div
									className='more-button'
									onClick={() => {
										const next2Items = apiData.slice(mobileEstimateList.length, mobileEstimateList.length + 2);
										setMobileEstimateList([...mobileEstimateList, ...next2Items]);
									}}>
									<div className='text'>看更多</div>
									<div className='icon-wrap'>
										<img src='/down.svg' alt='' />
									</div>
								</div>
							</div>
						)}
					</div>
				)}

				<div className='common-pad-wrap'>
					<div className='section-heading'>
						<div className='icon icon4'></div>
						<div className='text'>ETF最新公告</div>
					</div>
				</div>

				<div className='news-wrap'>
					{articlesMax3.map((article) => {
						const { guid, title, postTime, url, urlType } = article;
						const timestamp = getTimestamp(postTime || '');
						const date = moment(timestamp).format('YYYY/MM/DD');

						if (urlType === '_self') {
							return (
								<Link to={`${url || routeData.route + '/' + guid}`} className='article-link' key={guid}>
									<div className='title'>{title}</div>
									<div className='flex-between'>
										<div className='date'>{date}</div>
										<div className='more'>
											<div className='text'>看更多</div>
											<div className='icon-wrap'>
												<img src='/assets/link.svg' alt='' />
											</div>
										</div>
									</div>
								</Link>
							);
						}

						return (
							<a href={url} target={urlType} rel='noreferrer noopener' className='article-link' key={guid}>
								<div className='title'>{title}</div>
								<div className='flex-between'>
									<div className='date'>{date}</div>
									<div className='more'>
										<div className='text'>看更多</div>
										<div className='icon-wrap'>
											<img src='/assets/link.svg' alt='' />
										</div>
									</div>
								</div>
							</a>
						);
					})}
				</div>
			</div>

			<div className={`graph-popup-wrap ${popupOpen ? 'active' : ''}`}>
				<div className='graph-popup'>
					<div className='name'>{currentItemObj.twNameFull}</div>
					<div className='code'>{currentItemObj.etf002}</div>
					<div className='graph'>
						<ETFChart data={graphData} />
					</div>
					<div className='button-wrap'>
						<div
							className='button'
							onClick={() => {
								setPopupOpen(false);
							}}>
							關閉
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ETFLanding;
